import type {
  ApiResponse,
  ApiResponseEnvelope,
  PaginatedResponse,
} from '@/shared/api/types/api-response'
import type {
  CreateTicketFeaturePayload,
  ITicketFeature,
  TicketFeatureListParams,
  TicketFeatureListResponse,
  UpdateTicketFeaturePayload,
} from '@/shared/api/types/ticket-feature-types'
import { GW } from '@/shared/api/endpoints'
import { buildSkipLanguageHeaderConfig } from '@/shared/api/utils/skip-language-header'
import { unwrapApiEnvelope } from '@/shared/api/utils/unwrapapi'
import { request } from '@/shared/libs/request'

const TICKET_FEATURES_PATH = 'ticket-features'
export async function getTicketFeatures(
  params: TicketFeatureListParams,
): Promise<TicketFeatureListResponse> {
  const response = await request.get<ApiResponseEnvelope<PaginatedResponse<ITicketFeature>>>(
    GW.packaging(TICKET_FEATURES_PATH),
    {
      ...buildSkipLanguageHeaderConfig(),
      params: {
        current: params.current,
        pageSize: params.pageSize,
        ...(params.keywords ? { keywords: params.keywords } : {}),
      },
    },
  )
  const data = unwrapApiEnvelope(response.data)
  return {
    data: {
      data: data.data,
      total: data.total,
      current: data.current,
      pageSize: data.pageSize,
    },
  }
}

export async function createTicketFeature(
  payload: CreateTicketFeaturePayload,
): Promise<ApiResponse<ITicketFeature>> {
  const response = await request.post<ApiResponseEnvelope<ITicketFeature>>(
    GW.packaging(TICKET_FEATURES_PATH),
    payload,
  )
  return { data: unwrapApiEnvelope(response.data), message: 'Ticket feature created successfully' }
}

export async function updateTicketFeature(
  id: string,
  payload: UpdateTicketFeaturePayload,
): Promise<ApiResponse<ITicketFeature>> {
  const response = await request.patch<ApiResponseEnvelope<ITicketFeature>>(
    GW.packaging(`${TICKET_FEATURES_PATH}/${id}`),
    payload,
  )
  return { data: unwrapApiEnvelope(response.data), message: 'Ticket feature updated successfully' }
}

export async function toggleTicketFeatureActive(
  id: string,
  isActive: boolean,
): Promise<ApiResponse<ITicketFeature>> {
  const response = await request.put<ApiResponseEnvelope<ITicketFeature>>(
    GW.packaging(`${TICKET_FEATURES_PATH}/${id}`),
    { isActive },
  )
  return { data: unwrapApiEnvelope(response.data), message: 'Ticket feature status updated' }
}
